import { create } from "zustand";
import { isOffshore, magnitudeToParticleCount, nearestVolcano } from "./seismic";
import { playChime } from "./chime";

const MAX_PARTICLES = 16000;
const MAX_EVENTS = 3;
let _eid = 0;

function makeAftershocks(magnitude, duration) {
  const n = 3 + Math.floor(Math.random() * 3); // 3-5
  const arr = [];
  let t = duration + 2;
  for (let i = 0; i < n; i++) {
    arr.push({
      t,
      mag: Math.max(4, magnitude - (1.2 + Math.random() * 2)),
      latOff: (Math.random() - 0.5) * 6,
      lonOff: (Math.random() - 0.5) * 6,
    });
    t += 3 + Math.random() * 4;
  }
  return arr;
}

function computeEruption(ev) {
  if (ev.type === "volcanic") {
    return { lat: ev.lat, lon: ev.lon, name: ev.volcanoName || "Volcano", mag: ev.magnitude };
  }
  if (ev.magnitude >= 7.0) {
    const hit = nearestVolcano(ev.lat, ev.lon);
    if (hit) return { lat: hit.volcano.lat, lon: hit.volcano.lon, name: hit.volcano.name, mag: ev.magnitude };
  }
  return null;
}

export const useSim = create((set, get) => ({
  // editing buffer (bound to the selected event's params)
  magnitude: 7.4,
  duration: 45,
  depth: 12,
  soilType: "dense_sand",

  // multi-event workbench
  events: [], // [{ id, lat, lon, offshore, magnitude, duration, type, volcanoName, activeEruption, aftershocks }]
  selectedId: null,
  draft: null, // gold pin being placed { lat, lon, offshore }
  clickStage: 0, // 0 none, 1 draft placed (gold)
  pinMenuId: null, // event id whose on-globe menu is open

  // legacy mirror of the selected/draft source (kept for HUD + telemetry)
  epicenter: null,
  activeEruption: null,
  aftershocks: [],
  tsunamiEnabled: false,

  // uncertainty mode
  randomize: false,
  randomizing: false,

  // cinematic intro
  introPlaying: false,

  // lighting hub (shared by globe + street)
  timeOfDay: 13,
  autoCycle: false,
  liquefaction: 0.2,
  streetDay: false,

  // simulation clock (global — all events share a start)
  simulating: false,
  simStart: 0,
  simProgress: 0,
  includeAftershocks: false,

  // event-focus camera (street)
  focusNonce: 0,

  // view
  viewMode: "macro",
  canEnterStreet: false,

  // ui
  dropMode: true,
  drawerOpen: false,
  audioOn: false,

  // xr
  xrPresenting: false,

  maxParticles: MAX_PARTICLES,

  setMagnitude: (m) => {
    set({ magnitude: m });
    const { selectedId } = get();
    if (selectedId) get().updateEvent(selectedId, { magnitude: m });
  },
  setDuration: (d) => {
    set({ duration: d });
    const { selectedId } = get();
    if (selectedId) get().updateEvent(selectedId, { duration: d });
  },
  setDepth: (d) => set({ depth: d }),
  setSoilType: (s) => set({ soilType: s }),
  setDropMode: (v) => set({ dropMode: v }),
  setStreetDay: (v) => set({ streetDay: v }),
  setTimeOfDay: (v) => set({ timeOfDay: v }),
  setAutoCycle: (v) => set({ autoCycle: v }),
  setLiquefaction: (v) => set({ liquefaction: v }),
  setDayPreset: (mode) => set({ timeOfDay: mode === "sunny" ? 13 : 22, autoCycle: false }),
  setRandomize: (v) => set({ randomize: v }),
  setDrawerOpen: (v) => set({ drawerOpen: v }),
  setAudioOn: (v) => {
    if (typeof window !== "undefined") window.__audioOn = v;
    set({ audioOn: v });
  },
  setXrPresenting: (v) => set({ xrPresenting: v }),
  setCanEnterStreet: (v) => set({ canEnterStreet: v }),
  setPinMenuId: (id) => set({ pinMenuId: id }),

  startIntro: () => set({ introPlaying: true }),
  endIntro: () => set({ introPlaying: false }),

  // Guided Tour (auto-fly through famous quakes)
  tourActive: false,
  tourFocus: null,
  startTour: () => set({ introPlaying: false, tourActive: true, viewMode: "macro" }),
  setTourFocus: (f) => set({ tourFocus: f }),
  stopTour: () => {
    get().resetEpicenterFlow();
    set({ tourActive: false, tourFocus: null, viewMode: "macro" });
  },

  // --- Multi-event management ---
  addEvent: (e) => {
    const st = get();
    if (st.events.length >= MAX_EVENTS) return null;
    const id = `evt-${++_eid}`;
    const ev = {
      id,
      lat: e.lat,
      lon: e.lon,
      offshore: e.offshore ?? isOffshore(e.lat, e.lon),
      magnitude: e.magnitude ?? st.magnitude,
      duration: e.duration ?? st.duration,
      type: e.type || "seismic",
      volcanoName: e.volcanoName || null,
      activeEruption: null,
      aftershocks: [],
    };
    set({
      events: [...st.events, ev],
      selectedId: id,
      epicenter: { lat: ev.lat, lon: ev.lon, offshore: ev.offshore },
      magnitude: ev.magnitude,
      duration: ev.duration,
      tsunamiEnabled: ev.offshore,
    });
    return id;
  },

  updateEvent: (id, patch) => {
    set((st) => {
      const events = st.events.map((e) => {
        if (e.id !== id) return e;
        const merged = { ...e, ...patch };
        if (patch.lat !== undefined || patch.lon !== undefined) {
          merged.offshore = isOffshore(merged.lat, merged.lon);
        }
        return merged;
      });
      const sel = events.find((e) => e.id === st.selectedId);
      const mirror = sel
        ? {
            epicenter: { lat: sel.lat, lon: sel.lon, offshore: sel.offshore },
            tsunamiEnabled: sel.offshore,
          }
        : {};
      return { events, ...mirror };
    });
  },

  removeEvent: (id) => {
    set((st) => {
      const events = st.events.filter((e) => e.id !== id);
      const nextSel = st.selectedId === id ? (events[events.length - 1]?.id ?? null) : st.selectedId;
      const sel = events.find((e) => e.id === nextSel);
      return {
        events,
        selectedId: nextSel,
        pinMenuId: st.pinMenuId === id ? null : st.pinMenuId,
        epicenter: sel ? { lat: sel.lat, lon: sel.lon, offshore: sel.offshore } : null,
        magnitude: sel ? sel.magnitude : st.magnitude,
        duration: sel ? sel.duration : st.duration,
        tsunamiEnabled: sel ? sel.offshore : false,
        simulating: events.length ? st.simulating : false,
        activeEruption: null,
      };
    });
  },

  setSelectedId: (id) => {
    set((st) => {
      const sel = st.events.find((e) => e.id === id);
      if (!sel) return { selectedId: id };
      return {
        selectedId: id,
        epicenter: { lat: sel.lat, lon: sel.lon, offshore: sel.offshore },
        magnitude: sel.magnitude,
        duration: sel.duration,
        tsunamiEnabled: sel.offshore,
      };
    });
  },

  setFocusEvent: (id) => {
    get().setSelectedId(id);
    set((st) => ({ viewMode: "street", focusNonce: st.focusNonce + 1 }));
  },

  // Macro camera fly-to-pin (used by the Active Epicenters list on the globe)
  macroFocus: null,
  macroFocusNonce: 0,
  flyToEvent: (id) => {
    const e = get().events.find((x) => x.id === id);
    if (!e) return;
    get().setSelectedId(id);
    set((st) => ({ macroFocus: { lat: e.lat, lon: e.lon }, macroFocusNonce: st.macroFocusNonce + 1, viewMode: "macro" }));
  },

  // onboarding state mirror (so a VR head-locked slate can read the current step)
  onboardActive: false,
  onboardTitle: "",
  onboardBody: "",
  setOnboard: (o) => set(o),

  // Confirmation flow: 1st click = GOLD draft, 2nd click = commit to a slot (staged).
  handleGlobeClick: (lat, lon) => {
    const s = get();
    if (s.simulating) return;
    if (s.clickStage === 0) {
      if (s.events.length >= MAX_EVENTS) {
        playChime(300);
        return;
      }
      const offshore = isOffshore(lat, lon);
      set({
        draft: { lat, lon, offshore },
        clickStage: 1,
        epicenter: { lat, lon, offshore },
        tsunamiEnabled: offshore,
        pinMenuId: null,
      });
      playChime(660);
    } else if (s.clickStage === 1) {
      const d = s.draft;
      if (!d) return set({ clickStage: 0 });
      get().addEvent({ lat: d.lat, lon: d.lon, offshore: d.offshore, magnitude: s.magnitude, duration: s.duration, type: "seismic" });
      set({ draft: null, clickStage: 0 });
      playChime(1046);
    }
  },

  resetEpicenterFlow: () =>
    set({
      events: [],
      selectedId: null,
      draft: null,
      clickStage: 0,
      pinMenuId: null,
      epicenter: null,
      simulating: false,
      simProgress: 0,
      activeEruption: null,
      aftershocks: [],
      canEnterStreet: false,
      tsunamiEnabled: false,
    }),

  clearEpicenter: () => get().resetEpicenterFlow(),

  setTsunamiEnabled: (v) => set({ tsunamiEnabled: v }),
  setIncludeAftershocks: (v) => set({ includeAftershocks: v }),

  activeParticleCount: () => magnitudeToParticleCount(get().magnitude, MAX_PARTICLES),

  // Run every staged event simultaneously off one shared clock.
  executeAll: () => {
    const st = get();
    if (!st.events.length) return;
    const events = st.events.map((e) => ({
      ...e,
      activeEruption: computeEruption(e),
      aftershocks: st.includeAftershocks ? makeAftershocks(e.magnitude, e.duration) : [],
    }));
    const primary = events.reduce((a, b) => (b.magnitude > a.magnitude ? b : a), events[0]);
    const sel = events.find((e) => e.id === st.selectedId) || primary;
    set({
      events,
      simulating: true,
      simStart: performance.now(),
      simProgress: 0,
      pinMenuId: null,
      activeEruption: primary.activeEruption,
      aftershocks: sel.aftershocks,
    });
  },

  // Legacy single-run entry (presets / randomize). Ensures at least one event then runs all.
  startSim: () => {
    const st = get();
    if (!st.events.length && st.epicenter) {
      get().addEvent({ lat: st.epicenter.lat, lon: st.epicenter.lon, offshore: st.epicenter.offshore, magnitude: st.magnitude, duration: st.duration, type: "seismic" });
    }
    get().executeAll();
  },

  stopSim: () => set({ simulating: false, simProgress: 0, activeEruption: null }),

  // Rebuild City: smoothly restore the ruined metropolis (buildings/debris/flood/scorch
  // lerp back to intact) while KEEPING the staged epicenters so the user can re-run.
  rebuildCity: () => {
    set((st) => ({
      simulating: false,
      simProgress: 0,
      activeEruption: null,
      events: st.events.map((e) => ({ ...e, activeEruption: null, aftershocks: [] })),
    }));
    if (typeof window !== "undefined") {
      window.__streetScorch = 0;
      window.__flood = { active: false, level: -999, edge: -999, dirX: 0, dirZ: 0 };
      window.__disasterUntil = 0;
    }
  },

  tickSim: () => {
    const st = get();
    if (!st.simulating) return;
    const elapsed = (performance.now() - st.simStart) / 1000;
    let end = 0;
    let prog = 0;
    for (const e of st.events) {
      const afterEnd = e.aftershocks?.length ? e.aftershocks[e.aftershocks.length - 1].t + 3 : e.duration;
      end = Math.max(end, afterEnd);
      prog = Math.max(prog, Math.min(elapsed / e.duration, 1));
    }
    if (Math.abs(prog - st.simProgress) >= 0.02 || prog >= 1) set({ simProgress: prog });
    if (elapsed > end) set({ simulating: false, simProgress: 0, activeEruption: null });
  },

  setViewMode: (v) => set({ viewMode: v }),

  loadScenario: (s) => {
    get().resetEpicenterFlow();
    set({
      magnitude: s.magnitude,
      duration: s.duration,
      depth: s.depth,
      soilType: s.soil_type || "dense_sand",
      viewMode: "macro",
      drawerOpen: false,
    });
    get().addEvent({ lat: s.epicenter.lat, lon: s.epicenter.lon, offshore: s.epicenter.offshore, magnitude: s.magnitude, duration: s.duration, type: "seismic" });
  },

  // strongest currently-running (or staged) offshore event, for coastline flood
  strongestOffshore: () => {
    const st = get();
    let best = null;
    for (const e of st.events) {
      if (e.offshore && (!best || e.magnitude > best.magnitude)) best = e;
    }
    return best;
  },
}));
